import { useEffect, useState } from 'react';
import type { JSX } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { BellRing } from 'lucide-react';
import { useToast } from '@admin-platform/ui/components';

import { Button } from '../../Button/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Card/Card';
import Checkbox from '../../Checkbox/Checkbox';
import { SingleSelect } from '../../Inputs/SingleSelect';
import { Label } from '../../Inputs/Label';
import { NotificationCategory } from '../../../graphql/enums.graphql';
import { HOURS, WEEKDAYS } from '../../../pages/Settings/Notifications/Notifications';

import styles from './NotificationPreferences.module.css';

import MY_SETTINGS from '../queries/myNotificationSettings.query.graphql';
import type { MyNotificationSettingsQuery } from '../queries/myNotificationSettings.query.graphql';
import UPDATE_MY_SETTINGS from '../mutations/updateMyNotificationSettings.mutation.graphql';
import type {
  UpdateMyNotificationSettingsMutation,
  UpdateMyNotificationSettingsMutationVariables,
} from '../mutations/updateMyNotificationSettings.mutation.graphql';

/** Plain words for each category, since the enum name is not copy. */
const CATEGORY_LABEL: Record<string, string> = {
  CHECKIN: 'Check-in prompts',
  DIGEST: 'The weekly digest',
  TARGET_CHANGE: 'Changes to targets you own',
  TEAM: 'Team membership changes',
  ACCOUNT: 'Seats and invitations',
  ASSIGNMENT: 'Something became yours',
  CYCLE_STATE: 'A cycle opened, moved to scoring or closed',
};

/**
 * Account → Notifications.
 *
 * Two things a person controls: when their digest arrives, and which
 * categories they would rather not hear about. The two that cannot be silenced
 * are shown anyway, disabled, with the reason — a toggle that is simply absent
 * reads as an oversight rather than as a decision.
 */
export default function NotificationPreferences(): JSX.Element | null {
  const toast = useToast();
  const { data, refetch } = useQuery<MyNotificationSettingsQuery>(MY_SETTINGS, {
    fetchPolicy: 'cache-and-network',
  });
  const [updateSettings, { loading: saving }] = useMutation<
    UpdateMyNotificationSettingsMutation,
    UpdateMyNotificationSettingsMutationVariables
  >(UPDATE_MY_SETTINGS);

  const settings = data?.myNotificationSettings;
  const [day, setDay] = useState(1);
  const [hour, setHour] = useState(9);

  useEffect(() => {
    if (!settings) return;
    setDay(settings.day);
    setHour(settings.hour);
  }, [settings]);

  if (!settings) return null;

  const mutable = new Set(settings.mutableCategories);
  const muted = new Set(settings.mutedCategories);

  const save = async (
    variables: UpdateMyNotificationSettingsMutationVariables,
    message: string,
  ) => {
    try {
      await updateSettings({ variables });
      toast(message, 'success');
      await refetch();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save your preferences.', 'error');
    }
  };

  const toggleCategory = async (category: NotificationCategory) => {
    const next = new Set(muted);
    if (next.has(category)) next.delete(category);
    else next.add(category);
    await save({ mutedCategories: [...next] }, 'Preferences saved');
  };

  return (
    <Card noHover>
      <CardHeader>
        <div className={styles.Header}>
          <span aria-hidden="true">
            <BellRing />
          </span>
          <div>
            <CardTitle>Notifications</CardTitle>
            <CardDescription>
              When your digest arrives, and what you would rather not hear about.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className={styles.Body}>
        <div className={styles.Row}>
          <div className={styles.Field}>
            <Label htmlFor="my-digest-day">Digest day</Label>
            <SingleSelect
              id="my-digest-day"
              options={WEEKDAYS}
              value={day}
              onChange={(v) => setDay(Number(v))}
            />
          </div>
          <div className={styles.Field}>
            <Label htmlFor="my-digest-hour">Time</Label>
            <SingleSelect
              id="my-digest-hour"
              options={HOURS}
              value={hour}
              onChange={(v) => setHour(Number(v))}
            />
          </div>
          <Button
            variant="outline"
            disabled={saving}
            onClick={() => save({ digestDay: day, digestHour: hour }, 'Schedule saved')}
          >
            Save
          </Button>
        </div>
        <p className={styles.Hint}>
          {settings.source === 'TENANT'
            ? 'Following the workspace default. Saving makes it yours.'
            : 'Your own schedule, in your timezone.'}
        </p>

        <div className={styles.Categories}>
          {Object.values(NotificationCategory).map((category) => {
            const canMute = mutable.has(category);
            return (
              <div key={category} className={styles.CheckRow}>
                <Checkbox
                  id={`notif-cat-${category}`}
                  checked={!muted.has(category)}
                  disabled={!canMute || saving}
                  onChange={() => toggleCategory(category)}
                />
                <Label htmlFor={`notif-cat-${category}`}>
                  {CATEGORY_LABEL[category] ?? category}
                </Label>
                {!canMute && (
                  <span className={styles.Locked}>
                    Always sent — it tells you something you would otherwise act on without knowing
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
